import React, { useMemo, useState } from 'react';
import { JRCFormFieldRenderer } from '../Common/JRCFormFieldRenderer';
import { JRCInputSelectRaw } from './JRCInputSelectRaw';
import styled, { useTheme } from 'styled-components';
import { JRCAvatar } from '../../../JRCAvatar/JRCAvatar';
import { JRCTag } from '../../../JRCTag/JRCTag';
import { AutocompleteLittle, JRCAutocompleteProps, NativeAutocompleteProps } from './JRCInputSelect.types';
import { JRCModal } from 'components/JRCModal/templates/JRCModal';
import { JRCButton } from 'components/JRCButton/JRCButton';
import { useIntl } from 'react-intl';
import { FieldValues } from 'react-hook-form';

import { JRCInputAutocompleteViews } from './JRCInputAutocompleteViews';
import { JRCIcon } from '../../../JRCIcon/JRCIcon';
import { JRCEllipsis } from '../../../JRCEllipsis/JRCEllipsis';
import { JRCModalProps } from '../../../JRCModal/templates/JRCModal.types';

const Flex = styled.div`
    display: flex;
    align-items: center;
    min-height: 40px;
`;

const AutocompleteViewsButton = styled.div`
    display: flex;
    justify-content: flex-end;
    align-items: flex-end;

    > button {
        margin: 0 0 4px 4px;
    }
`;

const IconWrapper = styled.div`
    margin-left: -3px;
    margin-right: 6px;
    display: flex;
    align-items: center;
`;

const AvatarWrapper = styled.div`
    margin-right: 10px;
`;

const NewOptionIconWrapper = styled.div`
    margin-bottom: 1px;
    margin-left: 5px;
`;

export function CustomOption({
    option,
}: {
    option: AutocompleteLittle & { newOption?: boolean; custom?: boolean; icon?: string };
}) {
    const theme = useTheme();
    const [isAvatarOnError, setAvatarOnError] = useState(false);

    return (
        <Flex>
            {option.custom ? (
                <>
                    {option.icon ? (
                        <IconWrapper>
                            <JRCIcon name={option.icon} color={theme.color.primary} size={25} variant="circle" />
                        </IconWrapper>
                    ) : (
                        !isAvatarOnError && (
                            <JRCAvatar
                                onError={() => setAvatarOnError(true)}
                                alt="tag"
                                uri={option.uri}
                                focusable={true}
                            />
                        )
                    )}
                    <JRCEllipsis label={option.title} length={40} />
                </>
            ) : (
                <>
                    {option.newOption || (
                        <AvatarWrapper>
                            <JRCAvatar alt="tag" uri={option.uri} focusable={true} />
                        </AvatarWrapper>
                    )}
                    <JRCEllipsis label={option.title} length={40} />
                    {option.newOption && (
                        <NewOptionIconWrapper>
                            <JRCIcon name="icon-plus" color="primary" size={10} />
                        </NewOptionIconWrapper>
                    )}
                </>
            )}
        </Flex>
    );
}

export function CustomSelectedOption({ option }: { option: AutocompleteLittle & { icon?: string } }) {
    return (
        <JRCTag
            focusable
            uri={option.uri}
            label={option.title}
            tooltipDescription={option.title}
            defaultIcon={option.icon}
        />
    );
}

const NativeAutocompleteElement = function <T extends FieldValues = FieldValues>(
    { asyncPromise, defaultCustomOptions, ...props }: NativeAutocompleteProps<T>,
    _ref: React.ForwardedRef<HTMLInputElement>,
) {
    const asyncPromiseWithCustomOptions = useMemo(() => {
        if (!asyncPromise) return asyncPromise;
        const extraOptions = defaultCustomOptions ? defaultCustomOptions.map((o) => ({ ...o, custom: true })) : [];
        return (inputValue: string) => asyncPromise(inputValue).then((r) => [...r, ...extraOptions]);
    }, [asyncPromise, defaultCustomOptions]);

    return (
        <>
            <JRCInputSelectRaw
                getSearch="title"
                components={
                    props.components || {
                        Option: CustomOption,
                        SelectedOption: CustomSelectedOption,
                    }
                }
                {...props}
                asyncPromise={asyncPromiseWithCustomOptions}
            />
        </>
    );
};

export const NativeAutocomplete = React.forwardRef(NativeAutocompleteElement) as unknown as <
    T extends FieldValues = FieldValues,
>(
    props: NativeAutocompleteProps<T>,
) => JSX.Element;

export function JRCInputAutocomplete<T extends FieldValues = FieldValues>(props: JRCAutocompleteProps<T>) {
    const { name, control, rules, label, htmlFor, labelIsLegend, description, helper, margin } = props;
    const [open, setOpen] = useState<boolean>(false);
    const intl = useIntl();

    const argsModal: JRCModalProps = {
        open: open,
        title: intl.formatMessage({ id: 'PUBLISH_To' }),
        portalId: 'react-modal',
        closeHandler: () => {
            setOpen(false);
        },
    };

    return (
        <>
            <JRCFormFieldRenderer
                name={name}
                control={control}
                rules={rules}
                label={label}
                htmlFor={htmlFor}
                labelIsLegend={labelIsLegend}
                description={description}
                helper={helper}
                margin={margin}
                renderFunction={(field) => {
                    return props.views !== undefined && Array.isArray(props.views) && props.views.length ? (
                        <div style={{ display: 'flex', flexDirection: 'row' }}>
                            <div style={{ flex: 1 }}>
                                <NativeAutocomplete {...props} {...field} />
                            </div>
                            <AutocompleteViewsButton>
                                <JRCButton
                                    onClick={() => setOpen(true)}
                                    type="button"
                                    color="primary"
                                    icon={'icon-search'}
                                    variant="contained"
                                />
                            </AutocompleteViewsButton>
                        </div>
                    ) : (
                        <NativeAutocomplete {...props} {...field} />
                    );
                }}
            />
            {props.views !== undefined && Array.isArray(props.views) && props.views.length > 0 && (
                <JRCModal {...argsModal}>
                    <JRCInputAutocompleteViews
                        views={props.views}
                        initialValues={control._formValues[name]}
                        onConfirm={(values: any) => {
                            if (props.onConfirm) {
                                props.onConfirm(values);
                            }
                            setOpen(false);
                        }}
                    />
                </JRCModal>
            )}
        </>
    );
}
